import { record, upper, type Row } from '../ability/context';
import { inventoryField } from '../../shared/array-types';
import { readInventoryRows } from '../compile/inventory-raw-readers';
import { heldInventoryRows } from '../statuses/gm-boundary';
import type { RecoveryChanges } from '../resources/resource-recovery-plan';
import type { AppliedNewInventory, AppliedRowIdentity, AppliedTouchedRow } from './applied-frame';

/** Retire exact saved bodies/parts and move existing contents without compiling
 * a creature or copying any unrelated inventory subtree. */
export function recoveryAppliedRows(recovery: RecoveryChanges | undefined, encounter: Row, members: Map<string, Row>): {
    deletes: AppliedRowIdentity[]; inventory: AppliedNewInventory[]; rows: AppliedTouchedRow[];
} {
    if (!recovery) return { deletes: [], inventory: [], rows: [] };
    const inventory: AppliedNewInventory[] = [];
    const held = recovery.bodies.some(body => body.contents?.length) ? heldInventoryRows({ encounter, members }) : new Map<string, Row>();
    for (const body of recovery.bodies) for (const content of body.contents ?? []) {
        const child = held.get(content.inventory_id);
        if (!child) throw new Error('resource-recovery:content-not-held');
        const row = { ...record(child.inventory), possess_id: body.target };
        const node = { ...child, inventory: row };
        const base: Row[] = [{ inventory: row, statuses: child.statuses, charges: child.charges }];
        const walk = (parent: Row): void => {
            for (const nested of readInventoryRows(parent, 'inventory_in_container')) {
                base.push({ inventory: record(nested.inventory), statuses: nested.statuses, charges: nested.charges });
                walk(nested);
            }
        };
        walk(child);
        inventory.push({ entity_id: upper(inventoryField(child, 'inventory_id')), possess_id: body.target, node, base });
    }
    const retired = [...recovery.bodies, ...recovery.monsterBitRemovals];
    return { inventory, deletes: retired.map(row => ({ table: 'inventory', row_id: row.inventory_id })),
        rows: retired.map(row => ({ table: 'inventory', row_id: row.inventory_id, partial: true,
            row: { inventory_id: row.inventory_id, inventory_deleted: 1, item_qty: 0,
                ...('target' in row ? { inventory_encounter_creature_id: null } : {}) } })) };
}
